import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { VISIT_TRACKED_EVENT } from "@/lib/visitorSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Build the HTML tooltip shown when hovering a pin on the globe.
 * react-globe.gl renders the returned HTML string directly as a label.
 */
function formatPinTooltip(d) {
  if (!d) return "";
  const place = [d.city, d.region, d.country].filter(Boolean).join(", ");
  const liveTag = d.live
    ? '<span style="color:#dc2626;font-weight:bold;letter-spacing:.18em;">● LIVE NOW</span>'
    : '<span style="color:#a1a1aa;letter-spacing:.18em;">RECENT VISITOR</span>';
  return `
    <div style="background:#000;border:1px solid #dc2626;padding:8px 12px;font-family:monospace;color:#fafafa;font-size:12px;line-height:1.4;text-align:left;min-width:160px;">
      <div style="font-size:9px;letter-spacing:.25em;color:#a1a1aa;text-transform:uppercase;margin-bottom:4px;">//_listener</div>
      <div style="color:#fafafa;font-weight:bold;">${place || "Unknown location"}</div>
      <div style="font-size:10px;letter-spacing:.2em;text-transform:uppercase;margin-top:4px;">${liveTag}</div>
    </div>
  `;
}

/**
 * 3D rotating globe — paints a glowing red pulse for listeners online RIGHT
 * NOW, and a soft white dot for any city that has EVER tuned in.
 * Falls back gracefully (renders no pins) until /api/visits/locations has
 * data — geolocated visitor sessions get added the first time someone hits
 * the site.
 *
 * Uses react-globe.gl (three.js under the hood) — we lazy-resize on container
 * width and auto-rotate at a slow tempo so it feels alive without distracting.
 */
export default function ListenerGlobe() {
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const spotlightRef = useRef(null);
  const [pins, setPins] = useState([]);
  const [width, setWidth] = useState(800);
  const [ready, setReady] = useState(false);
  const [spotlight, setSpotlight] = useState(null);
  // Perf: only mount the WebGL globe when its container is actually on
  // screen. Prevents three.js from stealing CPU/GPU cycles from the sticky
  // ChatRoom sidebar (typing / send-lag bug reported by listeners). When
  // scrolled off-screen the globe unmounts completely → 0% GPU cost, all
  // main-thread cycles return to the chat. IntersectionObserver keeps this
  // in sync as the visitor scrolls.
  const [inView, setInView] = useState(false);

  // Track container width so the canvas scales with the page.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth || 800);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Mount/unmount the 3D globe based on viewport visibility. 200px rootMargin
  // means the globe hydrates *just* before it enters view (feels instant to
  // the user) but stays unmounted while they're scrolled up chatting.
  useEffect(() => {
    if (!containerRef.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true); // graceful fallback for very old browsers
      return;
    }
    const el = containerRef.current;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "200px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Fetch pins + arrivals + all-time stats; refresh every 30s and instantly
  // whenever this browser's own visit gets tracked.
  const [arrivals, setArrivals] = useState([]);
  const [globeStats, setGlobeStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [p, a, s] = await Promise.all([
        axios.get(`${API}/visits/locations`).catch(() => null),
        axios.get(`${API}/visits/arrivals?limit=10`).catch(() => null),
        axios.get(`${API}/visits/globe-stats`).catch(() => null),
      ]);
      if (cancelled) return;
      if (p) setPins(p.data || []);
      if (a) setArrivals(a.data || []);
      if (s) setGlobeStats(s.data);
    };
    load();
    const t = setInterval(load, 30 * 1000);
    window.addEventListener(VISIT_TRACKED_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener(VISIT_TRACKED_EVENT, load);
    };
  }, []);

  // Once globe instance is ready, enable auto-rotate.
  useEffect(() => {
    if (!ready || !globeRef.current) return;
    const controls = globeRef.current.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.35; // slow, hypnotic
      controls.enableZoom = false; // keeps users from accidentally breaking the layout
    }
    // Slight downward tilt — looks more cinematic than equator-flat.
    globeRef.current.pointOfView({ lat: 25, lng: -30, altitude: 2.2 }, 0);
  }, [ready]);

  // When the globe unmounts (scrolled off-screen) reset the ready flag so
  // the camera-setup effect above re-fires cleanly when it remounts.
  useEffect(() => {
    if (!inView) setReady(false);
  }, [inView]);

  // Pause auto-rotation when the cursor enters the globe so tooltip reads
  // stay readable. Without this, the globe keeps spinning under the cursor
  // and the pin slides out → tooltip disappears in a blink.
  const handleMouseEnter = () => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls();
    if (controls) controls.autoRotate = false;
  };
  const handleMouseLeave = () => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls();
    if (controls) controls.autoRotate = true;
  };

  const liveCount = pins.filter((p) => p.live).length;
  const totalCount = pins.length;
  // Unique CITIES + COUNTRIES — dedupe by identifier so hitting the 500-pin
  // cap doesn't freeze the counters. Without this, the "CITIES" stat just
  // reports `pins.length` which caps at GLOBE_HISTORY_LIMIT and never grows
  // once the cap is hit, even as new distinct cities join.
  const uniqueCities = new Set(
    pins
      .map((p) => `${(p.city || "").toLowerCase()}|${(p.country_code || "").toLowerCase()}`)
      .filter((k) => k !== "|"),
  ).size;
  const countries = new Set(pins.map((p) => p.country_code).filter(Boolean));

  // Two pin layers: dim history + bright pulsing live.
  const livePins = pins.filter((p) => p.live);
  const historyPins = pins.filter((p) => !p.live);

  // "TUNING IN" spotlight — rotate a random listener pin every 4s and fire
  // amber arcs to it from a handful of other pins. Story: "The station is
  // where the world is meeting up right now." Amber (not red) so we don't
  // step on the DJ globe's signature red arcs.
  useEffect(() => {
    if (pins.length < 2) {
      setSpotlight(null);
      return;
    }
    // Pause the spotlight recompute loop when the globe is off-screen — no
    // point recalculating arcs nobody can see, and it keeps the main thread
    // free for the sticky ChatRoom.
    if (!inView) return;
    const pickSpotlight = () => {
      // Prefer live pins if there are at least 2 so the spotlight always
      // lands on someone actively tuned in — falls back to all pins otherwise.
      const live = pins.filter((p) => p.live);
      const pool = live.length >= 2 ? live : pins;
      let next = pool[Math.floor(Math.random() * pool.length)];
      // Avoid repeating the same spotlight twice in a row.
      if (pool.length > 1 && spotlightRef.current) {
        let guard = 0;
        while (
          next &&
          next.lat === spotlightRef.current.lat &&
          next.lng === spotlightRef.current.lng &&
          guard < 4
        ) {
          next = pool[Math.floor(Math.random() * pool.length)];
          guard += 1;
        }
      }
      spotlightRef.current = next;
      setSpotlight(next);
    };
    pickSpotlight();
    const t = setInterval(pickSpotlight, 4000);
    return () => clearInterval(t);
  }, [pins, inView]);

  // Build the arc list: 12 random source pins pointing to the spotlight.
  // Recomputed whenever the spotlight changes, so every cycle looks fresh.
  const arcs = (() => {
    if (!spotlight || pins.length < 2) return [];
    const others = pins.filter(
      (p) => !(p.lat === spotlight.lat && p.lng === spotlight.lng)
    );
    if (others.length === 0) return [];
    const shuffled = [...others].sort(() => Math.random() - 0.5);
    const sources = shuffled.slice(0, Math.min(12, others.length));
    return sources.map((src) => ({
      startLat: src.lat,
      startLng: src.lng,
      endLat: spotlight.lat,
      endLng: spotlight.lng,
    }));
  })();

  // Gold pulse ring on the spotlight target so the "signal converging" story
  // reads at a glance — a single, tall, slower ring in gold, painted on top
  // of the existing red LIVE listener rings.
  const spotlightRings = spotlight ? [spotlight] : [];

  return (
    <div data-testid="listener-globe-section">
      {/* Header strip */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
            //_LIVE_LISTENER_GLOBE
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight">
            EVERY PIN <span className="text-red-600">IS A LISTENER</span>.
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center" data-testid="globe-stats">
          <Stat label="LIVE NOW" value={liveCount} accent="red" data-testid="globe-stat-live" />
          <Stat label="CITIES" value={globeStats ? globeStats.cities : uniqueCities} data-testid="globe-stat-cities" />
          <Stat label="COUNTRIES" value={globeStats ? globeStats.countries : countries.size} data-testid="globe-stat-countries" />
        </div>
      </div>

      {/* Globe canvas */}
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative border border-zinc-800 bg-black overflow-hidden"
        style={{ height: "min(560px, 70vw)" }}
        data-testid="listener-globe-canvas"
      >
        {inView ? (
          <Globe
            ref={globeRef}
            width={width}
            height={Math.min(560, width * 0.7)}
            backgroundColor="rgba(0,0,0,0)"
            globeImageUrl="https://unpkg.com/three-globe@2.30.1/example/img/earth-night.jpg"
            showAtmosphere
            atmosphereColor="#dc2626"
            atmosphereAltitude={0.18}
            /* History pins — small, soft white dots */
            pointsData={historyPins}
            pointLat={(d) => d.lat}
            pointLng={(d) => d.lng}
            pointColor={() => "#fafafa"}
            pointAltitude={0.01}
            pointRadius={0.35}
            pointLabel={(d) => formatPinTooltip(d)}
            /* Live pins + spotlight target — merged into one rings layer.
               Live listeners pulse RED. The current spotlight (recipient of
               all the amber arcs) pulses a slower, taller GOLD ring so the
               "signal converging here" story reads at a glance. */
            ringsData={[
              ...livePins.map((p) => ({ ...p, _kind: "live" })),
              ...spotlightRings.map((p) => ({ ...p, _kind: "spotlight" })),
            ]}
            ringLat={(d) => d.lat}
            ringLng={(d) => d.lng}
            ringMaxRadius={(d) => (d._kind === "spotlight" ? 4 : 2.5)}
            ringPropagationSpeed={(d) => (d._kind === "spotlight" ? 2.2 : 1.5)}
            ringRepeatPeriod={(d) => (d._kind === "spotlight" ? 1400 : 1200)}
            ringColor={(d) => (t) =>
              d._kind === "spotlight"
                ? `rgba(253, 224, 71, ${1 - t})`
                : `rgba(220, 38, 38, ${1 - t})`
            }
            /* "TUNING IN" spotlight arcs — signal-beam threads converging on
               the current spotlight pin. White at the source, gold at the
               destination = classic radio-wave "signal travelling" gradient.
               Different palette from the DJ globe (pure red) so the two
               visualisations complement instead of compete. */
            arcsData={arcs}
            arcStartLat={(d) => d.startLat}
            arcStartLng={(d) => d.startLng}
            arcEndLat={(d) => d.endLat}
            arcEndLng={(d) => d.endLng}
            arcColor={() => ["rgba(255, 255, 255, 0.85)", "rgba(253, 224, 71, 1)"]}
            arcStroke={0.65}
            arcDashLength={0.5}
            arcDashGap={0.25}
            arcDashInitialGap={() => Math.random()}
            arcDashAnimateTime={1800}
            arcAltitudeAutoScale={0.7}
            /* Invisible hover layer for live pins so you can read their city
               too — react-globe.gl rings don't accept tooltips on their own. */
            customLayerData={livePins}
            customLayerLat={(d) => d.lat}
            customLayerLng={(d) => d.lng}
            customLayerLabel={(d) => formatPinTooltip(d)}
            customThreeObject={() => {
              const g = new THREE.SphereGeometry(0.9, 12, 12);
              const m = new THREE.MeshBasicMaterial({
                color: 0xdc2626,
                transparent: true,
                opacity: 0,
              });
              return new THREE.Mesh(g, m);
            }}
            onGlobeReady={() => setReady(true)}
          />
        ) : (
          /* Off-screen placeholder — zero GPU cost. Renders while the visitor
             is scrolled elsewhere (chatting, reading the hero, etc.) so we
             don't burn CPU on WebGL nobody can see. */
          <div
            className="w-full h-full flex items-center justify-center text-zinc-600 font-mono text-xs tracking-[0.4em]"
            data-testid="listener-globe-standby"
          >
            //_SIGNAL_STANDBY
          </div>
        )}

        {/* "TUNING IN" spotlight indicator — cycles every 4s, mirrors the
            //_SIGNAL_LOCK card on the DJ globe but in amber to differentiate. */}
        {spotlight && (
          <div
            className="absolute top-3 right-3 border border-amber-400 bg-black/85 backdrop-blur-sm px-3 py-2 pointer-events-none max-w-[280px]"
            data-testid="tuning-in-spotlight"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <div className="text-[9px] tracking-[0.3em] text-amber-400 uppercase font-mono">
                //_tuning_in
              </div>
            </div>
            <div className="mt-1 text-xs text-zinc-50 font-mono truncate">
              {[spotlight.city, spotlight.country].filter(Boolean).join(", ") ||
                "Anonymous listener"}
            </div>
          </div>
        )}

        {/* Empty-state hint while we have no geolocation data yet */}
        {pins.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center px-6">
              <div className="text-xs tracking-[0.4em] text-zinc-500 uppercase mb-2">
                //_WAITING_FOR_FIRST_PIN
              </div>
              <p className="text-zinc-400 text-sm max-w-sm">
                The globe lights up as listeners tune in. First visit from
                each location drops a pin.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 text-[10px] text-zinc-600 tracking-widest uppercase">
        //_pins_are_city_level · anonymous · refreshes_every_30_seconds · hover_a_pin_for_details
      </div>

      <ArrivalsFeed arrivals={arrivals} />
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function ArrivalsFeed({ arrivals }) {
  if (!arrivals.length) return null;
  return (
    <div className="mt-6 border border-zinc-800 bg-zinc-900/40" data-testid="recent-pins-feed">
      <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
        <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase">//_JUST_TUNED_IN</div>
        <div className="text-[10px] tracking-widest text-zinc-500 uppercase">latest_arrivals</div>
      </div>
      <ul className="divide-y divide-zinc-800" data-testid="recent-pins-list">
        {arrivals.map((a, i) => {
          const fresh = Date.now() - new Date(a.arrived_at).getTime() < 5 * 60 * 1000;
          return (
            <li
              key={`${a.arrived_at}-${i}`}
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zinc-900/70 transition-colors"
              data-testid={`recent-pin-row-${i}`}
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${fresh ? "bg-red-600 animate-pulse" : "bg-zinc-500"}`}
                aria-label={fresh ? "Live" : "Recent"}
              />
              <span className="font-mono text-zinc-100 truncate">
                {[a.city, a.region, a.country].filter(Boolean).join(" · ") || "Unknown location"}
              </span>
              {a.returning && (
                <span className="text-[10px] tracking-widest uppercase text-amber-400 shrink-0" data-testid={`recent-pin-returning-${i}`}>
                  RETURNING
                </span>
              )}
              <span
                className={`ml-auto text-[10px] tracking-widest uppercase shrink-0 ${fresh ? "text-red-500 font-display" : "text-zinc-500"}`}
                data-testid={`recent-pin-time-${i}`}
              >
                {fresh ? "● LIVE · " : ""}{timeAgo(a.arrived_at)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Stat({ label, value, accent, "data-testid": testId }) {
  const color = accent === "red" ? "text-red-600" : "text-zinc-50";
  return (
    <div
      className="border border-zinc-800 px-3 py-2 min-w-[80px] bg-zinc-900/40"
      data-testid={testId}
    >
      <div className={`font-display text-2xl md:text-3xl leading-none ${color}`}>
        {value}
      </div>
      <div className="text-[9px] tracking-[0.25em] text-zinc-400 uppercase mt-1">
        {label}
      </div>
    </div>
  );
}
